import { useEffect, useMemo, useState } from 'react';
import { Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useAppData } from '@/contexts/AppDataContext';
import { useStore } from '@/contexts/StoreContext';
import { secureGetItem, secureRemoveItem } from '@/lib/storage';
import { VehicleCarouselSection } from './VehicleCarouselSection';

interface RecentlyViewedVehiclesProps {
  config: any;
}

interface ViewedVehicle {
  id: number;
  timestamp: number;
}

export const RecentlyViewedVehicles = ({ config }: RecentlyViewedVehiclesProps) => {
  const { vehicles } = useAppData();
  const { selectedStore } = useStore();
  const [recentlyViewed, setRecentlyViewed] = useState<any[]>([]);

  const shouldFilterByStore = useMemo(() => {
    const unifiedAds = config?.unifiedAds !== false;
    const unifiedAdsStore = config?.unifiedAdsStore;
    const selectedStoreId = selectedStore?._id;

    if (!unifiedAds) return true;
    if (unifiedAdsStore === null || unifiedAdsStore === undefined) return false;
    return selectedStoreId !== unifiedAdsStore;
  }, [config?.unifiedAds, config?.unifiedAdsStore, selectedStore?._id]);

  useEffect(() => {
    const loadRecentlyViewed = async () => {
      try {
        const viewedStr = await secureGetItem('recentlyViewedVehicles');
        if (!viewedStr) {
          setRecentlyViewed([]);
          return;
        }

        const viewedIds: ViewedVehicle[] = JSON.parse(viewedStr);
        const sortedIds = viewedIds
          .sort((a, b) => b.timestamp - a.timestamp)
          .slice(0, 12);

        let viewedVehicles = sortedIds
          .map((viewed) => vehicles.find((v) => v.id === viewed.id))
          .filter((vehicle): vehicle is NonNullable<typeof vehicle> => vehicle != null);

        if (shouldFilterByStore && selectedStore?._id) {
          viewedVehicles = viewedVehicles.filter((vehicle) => vehicle.store === selectedStore._id);
        }

        setRecentlyViewed(viewedVehicles);
      } catch (error) {
        console.error('Erro ao carregar veículos vistos recentemente:', error);
        setRecentlyViewed([]);
      }
    };

    loadRecentlyViewed();

    const handleStorageChange = () => {
      loadRecentlyViewed();
    };

    window.addEventListener('vehicleViewed', handleStorageChange);
    return () => window.removeEventListener('vehicleViewed', handleStorageChange);
  }, [vehicles, shouldFilterByStore, selectedStore?._id]);

  const clearRecentlyViewed = async () => {
    await secureRemoveItem('recentlyViewedVehicles');
    setRecentlyViewed([]);
    window.dispatchEvent(new Event('vehicleViewed'));
  };

  if (recentlyViewed.length === 0) {
    return null;
  }

  return (
    <VehicleCarouselSection
      title="Vistos recentemente"
      viewAllHref="/estoque"
      vehicles={recentlyViewed}
      muted
      headerAction={
        <Button
          variant="outline"
          size="sm"
          onClick={clearRecentlyViewed}
          className="wl6-featured-clear-btn rounded-lg shrink-0"
        >
          <Trash2 className="h-4 w-4 sm:mr-1.5" />
          <span className="hidden sm:inline">Limpar</span>
        </Button>
      }
    />
  );
};
